// Supabase-backed CRUD adapter. Mirrors legacy db-remote.js API so legacy
// view IIFEs (raw-viewer.js, calc-studio.js, …) keep working.

import type { SupabaseClient, PostgrestError } from '@supabase/supabase-js';
import { STORES } from './types';
import { explainError, type SupaWrapper } from '../supabase-client';

export interface RemoteDb {
  _isRemote: true;
  _backend: 'supabase';
  STORES: typeof STORES;
  openDB(): Promise<null>;
  get<T = unknown>(table: string, id: string): Promise<T | null>;
  getAll<T = unknown>(table: string, filterFn?: (r: T) => boolean): Promise<T[]>;
  put<T = unknown>(table: string, record: T): Promise<T>;
  bulkPut<T = unknown>(table: string, records: T[]): Promise<number>;
  del(table: string, id: string): Promise<void>;
  count(table: string): Promise<number>;
  clear(table: string): Promise<void>;
  seedIfEmpty(onProgress?: (line: string) => void): Promise<number>;
  exportAll(): Promise<unknown>;
  importAll(
    payload: { stores: Record<string, unknown[]> },
    opts?: { wipe?: boolean },
  ): Promise<number>;
  genId(prefix: string): string;
}

function pk(table: string): string {
  const s = STORES[table];
  if (!s) throw new Error(`unknown table: ${table}`);
  return s.keyPath;
}

function wrap(error: PostgrestError | null, op: string, table: string): Error | null {
  if (!error) return null;
  return new Error(`[supabase ${op} ${table}] ${explainError(error)}`);
}

export function createRemoteDb(supa: SupaWrapper): RemoteDb {
  function sb(): SupabaseClient {
    if (!supa.client) throw new Error('Supabase client belum siap.');
    return supa.client;
  }

  async function get<T>(table: string, id: string): Promise<T | null> {
    const { data, error } = await sb().from(table).select('*').eq(pk(table), id).maybeSingle();
    if (error && error.code !== 'PGRST116') throw wrap(error, 'get', table);
    return (data as T) ?? null;
  }

  async function getAll<T>(table: string, filterFn?: (r: T) => boolean): Promise<T[]> {
    if (!STORES[table]) throw new Error(`unknown table: ${table}`);
    const PAGE = 1000;
    let from = 0;
    const out: T[] = [];
    while (true) {
      const { data, error } = await sb()
        .from(table)
        .select('*')
        .range(from, from + PAGE - 1);
      if (error) throw wrap(error, 'getAll', table);
      const rows = (data ?? []) as T[];
      if (rows.length === 0) break;
      out.push(...rows);
      if (rows.length < PAGE) break;
      from += PAGE;
    }
    return filterFn ? out.filter(filterFn) : out;
  }

  async function put<T>(table: string, record: T): Promise<T> {
    if (!STORES[table]) throw new Error(`unknown table: ${table}`);
    const r = { ...(record as object) } as Record<string, unknown>;
    if (table !== 'settings') r.updated_at = new Date().toISOString();
    const session = await supa.getSession();
    if (
      session.user?.id &&
      !('created_by' in r) &&
      table !== 'settings' &&
      table !== 'audit_logs'
    ) {
      r.created_by = session.user.id;
    }
    const { data, error } = await sb().from(table).upsert(r).select().maybeSingle();
    if (error) throw wrap(error, 'put', table);
    return (data ?? r) as T;
  }

  async function bulkPut<T>(table: string, records: T[]): Promise<number> {
    if (!STORES[table]) throw new Error(`unknown table: ${table}`);
    if (!Array.isArray(records) || records.length === 0) return 0;
    const CHUNK = 500;
    let total = 0;
    for (let i = 0; i < records.length; i += CHUNK) {
      const chunk = records.slice(i, i + CHUNK).map((r) => ({
        ...(r as object),
        updated_at: (r as { updated_at?: string }).updated_at ?? new Date().toISOString(),
      }));
      const { error } = await sb().from(table).upsert(chunk);
      if (error) throw wrap(error, 'bulkPut', table);
      total += chunk.length;
    }
    return total;
  }

  async function del(table: string, id: string): Promise<void> {
    const { error } = await sb().from(table).delete().eq(pk(table), id);
    if (error) throw wrap(error, 'del', table);
  }

  async function count(table: string): Promise<number> {
    if (!STORES[table]) throw new Error(`unknown table: ${table}`);
    const { count: c, error } = await sb().from(table).select('*', { count: 'exact', head: true });
    if (error) {
      console.warn('[db-remote] count blocked:', wrap(error, 'count', table)?.message);
      return 0;
    }
    return c ?? 0;
  }

  async function clear(table: string): Promise<void> {
    const { error } = await sb().from(table).delete().neq(pk(table), '__never_match__');
    if (error) throw wrap(error, 'clear', table);
  }

  async function seedIfEmpty(onProgress?: (line: string) => void): Promise<number> {
    const log = (m: string) => {
      onProgress?.(m);
      console.info('[seed:remote]', m);
    };
    const fetches: Array<[string, string]> = [
      ['assets', 'data/assets.json'],
      ['yearly_observations', 'data/yearly_observations.json'],
      ['indicators', 'data/indicators.json'],
    ];
    let seeded = 0;
    for (const [t, url] of fetches) {
      let c = 0;
      try {
        c = await count(t);
      } catch (_) {
        /* noop */
      }
      if (c > 0) {
        log(`${t}: ${c} record sudah ada — skip seed`);
        continue;
      }
      log(`${t}: kosong — coba seed dari ${url}`);
      try {
        const res = await fetch(url);
        if (!res.ok) {
          log(`${t}: fetch ${url} gagal (${res.status})`);
          continue;
        }
        const data = (await res.json()) as unknown[];
        await bulkPut(t, data);
        log(`${t}: seeded ${data.length} ke Supabase`);
        seeded += data.length;
      } catch (e) {
        log(`${t}: seed dilewati — ${(e as Error).message}`);
      }
    }
    return seeded;
  }

  async function exportAll(): Promise<unknown> {
    const out: { exported_at: string; stores: Record<string, unknown[]> } = {
      exported_at: new Date().toISOString(),
      stores: {},
    };
    for (const name of Object.keys(STORES)) {
      try {
        out.stores[name] = await getAll(name);
      } catch (e) {
        out.stores[name] = [];
        console.warn(`[exportAll] ${name}: ${(e as Error).message}`);
      }
    }
    return out;
  }

  async function importAll(
    payload: { stores: Record<string, unknown[]> },
    opts: { wipe?: boolean } = {},
  ): Promise<number> {
    if (!payload?.stores) throw new Error('invalid backup payload');
    if (opts.wipe) {
      for (const name of Object.keys(STORES)) {
        try {
          await clear(name);
        } catch (e) {
          console.warn(`[importAll] clear ${name}: ${(e as Error).message}`);
        }
      }
    }
    let total = 0;
    for (const [name, records] of Object.entries(payload.stores)) {
      if (!STORES[name] || !records?.length) continue;
      try {
        total += await bulkPut(name, records);
      } catch (e) {
        console.warn(`[importAll] ${name}: ${(e as Error).message}`);
      }
    }
    return total;
  }

  function genId(prefix: string): string {
    const t = Date.now().toString(36);
    const r = Math.random().toString(36).slice(2, 6);
    return `${prefix}-${t}-${r}`.toUpperCase();
  }

  async function openDB(): Promise<null> {
    return null;
  }

  return {
    _isRemote: true,
    _backend: 'supabase',
    STORES,
    openDB,
    get,
    getAll,
    put,
    bulkPut,
    del,
    count,
    clear,
    seedIfEmpty,
    exportAll,
    importAll,
    genId,
  };
}
